/**
 * WhatsApp Audiences TanStack Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  waApi,
  type WAAudiencesListParams,
  type WAAudiencesListResponse,
  type WAAudienceStats,
  type WAAudience,
  type WAAudienceCreateInput,
  type WAAudienceUpdateInput,
  type WAAudiencePreview,
  type WAAudienceContactSearchResult,
} from '@/lib/api-client'

// Query keys
export const waAudiencesKeys = {
  all: ['wa-audiences'] as const,
  lists: () => [...waAudiencesKeys.all, 'list'] as const,
  list: (params?: WAAudiencesListParams) => [...waAudiencesKeys.lists(), params] as const,
  stats: () => [...waAudiencesKeys.all, 'stats'] as const,
  details: () => [...waAudiencesKeys.all, 'detail'] as const,
  detail: (id: number) => [...waAudiencesKeys.details(), id] as const,
  previews: () => [...waAudiencesKeys.all, 'preview'] as const,
  preview: (id: number) => [...waAudiencesKeys.previews(), id] as const,
  contactSearch: (search: string) => [...waAudiencesKeys.all, 'contact-search', search] as const,
  branches: () => [...waAudiencesKeys.all, 'branches'] as const,
}

/**
 * Hook to fetch branches for audience filters
 */
export function useWABranches() {
  return useQuery({
    queryKey: waAudiencesKeys.branches(),
    queryFn: () => waApi.audiences.branches(),
    staleTime: 5 * 60 * 1000, // 5 min — branches rarely change
  })
}

/**
 * Hook to fetch list of audiences
 */
export function useWAAudiences(params?: WAAudiencesListParams) {
  return useQuery<WAAudiencesListResponse>({
    queryKey: waAudiencesKeys.list(params),
    queryFn: () => waApi.audiences.list(params),
    staleTime: 30 * 1000,
  })
}

/**
 * Hook to fetch audience stats
 */
export function useWAAudienceStats() {
  return useQuery<WAAudienceStats>({
    queryKey: waAudiencesKeys.stats(),
    queryFn: () => waApi.audiences.stats(),
    staleTime: 60 * 1000,
  })
}

/**
 * Hook to fetch single audience
 */
export function useWAAudience(id: number | null) {
  return useQuery<WAAudience>({
    queryKey: waAudiencesKeys.detail(id ?? 0),
    queryFn: () => waApi.audiences.get(id!),
    enabled: !!id,
  })
}

/**
 * Hook to create audience
 */
export function useCreateWAAudience() {
  const queryClient = useQueryClient()

  return useMutation<WAAudience, Error, WAAudienceCreateInput>({
    mutationFn: (data) => waApi.audiences.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: waAudiencesKeys.all })
    },
  })
}

/**
 * Hook to update audience
 */
export function useUpdateWAAudience() {
  const queryClient = useQueryClient()

  return useMutation<WAAudience, Error, { id: number; data: WAAudienceUpdateInput }>({
    mutationFn: ({ id, data }) => waApi.audiences.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: waAudiencesKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: waAudiencesKeys.lists() })
    },
  })
}

/**
 * Hook to delete audience
 */
export function useDeleteWAAudience() {
  const queryClient = useQueryClient()

  return useMutation<{ success: boolean; message: string; deletedId: number }, Error, number>({
    mutationFn: (id) => waApi.audiences.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: waAudiencesKeys.all })
    },
  })
}

/**
 * Hook to preview audience contacts
 */
export function usePreviewWAAudience() {
  return useMutation<WAAudiencePreview, Error, number>({
    mutationFn: (id) => waApi.audiences.preview(id),
  })
}

/**
 * Hook to snapshot (freeze) dynamic audience to static
 */
export function useSnapshotWAAudience() {
  const queryClient = useQueryClient()

  return useMutation<{ success: boolean; message: string; audience: WAAudience }, Error, number>({
    mutationFn: (id) => waApi.audiences.snapshot(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: waAudiencesKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: waAudiencesKeys.lists() })
    },
  })
}

/**
 * Hook to search contacts for manual audience picker
 */
export function useSearchAudienceContacts(search: string) {
  return useQuery<{ data: WAAudienceContactSearchResult[] }>({
    queryKey: waAudiencesKeys.contactSearch(search),
    queryFn: () => waApi.audiences.searchContacts(search),
    enabled: search.length >= 2,
    staleTime: 10 * 1000,
  })
}
